import { renderFloorPlanFrame } from './floorPlanThreePostProcessing.js';

/**
 * @typedef {{ width: number; height: number; left: number; top: number }} CaptureRect
 */

/**
 * Crop the rendered WebGL viewport to the active capture rectangle and scale to GPT output size.
 *
 * @param {{ renderer: import('three').WebGLRenderer; scene: import('three').Scene; camera: import('three').Camera; composer?: import('three/examples/jsm/postprocessing/EffectComposer.js').EffectComposer | null; isDisposed?: boolean }} sceneState
 * @param {CaptureRect} captureRect CSS-pixel rect relative to the renderer canvas client box
 * @param {number} outputWidth
 * @param {number} outputHeight
 * @param {number} [jpegQuality=0.92]
 * @returns {Promise<Blob>}
 */
export async function captureRoomRenderViewport(
  sceneState,
  captureRect,
  outputWidth,
  outputHeight,
  jpegQuality = 0.92,
) {
  if (!sceneState?.renderer || sceneState.isDisposed) {
    throw new Error('The 3D preview is not ready.');
  }
  if (!captureRect?.width || !captureRect?.height) {
    throw new Error('The capture frame is not ready.');
  }
  if (!outputWidth || !outputHeight) {
    throw new Error('Output size is invalid.');
  }

  renderFloorPlanFrame(sceneState);

  const canvas = sceneState.renderer.domElement;
  const clientWidth = canvas.clientWidth || captureRect.width;
  const clientHeight = canvas.clientHeight || captureRect.height;
  const scaleX = canvas.width / clientWidth;
  const scaleY = canvas.height / clientHeight;

  const sourceX = Math.round(captureRect.left * scaleX);
  const sourceY = Math.round(captureRect.top * scaleY);
  const sourceWidth = Math.round(captureRect.width * scaleX);
  const sourceHeight = Math.round(captureRect.height * scaleY);

  const outputCanvas = document.createElement('canvas');
  outputCanvas.width = outputWidth;
  outputCanvas.height = outputHeight;
  const ctx = outputCanvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not create canvas context.');
  }

  ctx.drawImage(
    canvas,
    sourceX,
    sourceY,
    sourceWidth,
    sourceHeight,
    0,
    0,
    outputWidth,
    outputHeight,
  );

  return new Promise((resolve, reject) => {
    outputCanvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Could not encode viewport capture as JPEG.'));
        else resolve(blob);
      },
      'image/jpeg',
      jpegQuality,
    );
  });
}
